import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Box,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  SlideUpTransition,
  useMinimizable,
  MinimizeButton,
  MinimizedBar,
} from '../../components/MinimizableDialog';
import { Client } from '../../types/client';
import { Service } from '../../types/service';
import { ModePaiement } from '../../types/modePaiement';
import { Projet } from '../../types/projet';
import { Devis } from '../../types/devis';
import { EncaissementCreate, EncaissementUpdate, MotifEncaissement, TypeEncaissement, StatusEncaissement, FactureImpayee } from '../../types/encaissement';
import { encaissementsAPI, clientsAPI, servicesAPI, modesPaiementAPI, projetsAPI, devisAPI } from '../../services/api';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import useFormDraft from '../../hooks/useFormDraft';
import { extractErrorMessage } from '../../utils/errorHandler';

interface EncaissementFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  encaissementId?: number;
  showStatus?: boolean;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const EncaissementFormDialog: React.FC<EncaissementFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  encaissementId,
  showStatus = false,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const [loading, setLoading] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );
  const [clients, setClients] = useState<Client[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [modesPaiement, setModesPaiement] = useState<ModePaiement[]>([]);
  const [projets, setProjets] = useState<Projet[]>([]);
  const [devisValides, setDevisValides] = useState<Devis[]>([]);
  const [facturesImpayees, setFacturesImpayees] = useState<FactureImpayee[]>([]);

  // Form state
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedModePaiement, setSelectedModePaiement] = useState<ModePaiement | null>(null);
  const [referencePaiement, setReferencePaiement] = useState('');
  const [typeEncaissement, setTypeEncaissement] = useState<string>(TypeEncaissement.TOTALITE);
  const [motifEncaissement, setMotifEncaissement] = useState<string>(MotifEncaissement.PAIEMENT_FACTURE);
  const [statusEncaissement, setStatusEncaissement] = useState<string>(StatusEncaissement.EN_COURS);
  const [selectedFacture, setSelectedFacture] = useState<FactureImpayee | null>(null);
  const [selectedDevis, setSelectedDevis] = useState<Devis | null>(null);
  const [dejaEncaisse, setDejaEncaisse] = useState<number>(0);
  const [libelle, setLibelle] = useState('');
  const [montantEncaisse, setMontantEncaisse] = useState<string>('');
  const [reste, setReste] = useState<string>('');
  const [dateEcheance, setDateEcheance] = useState<string>('');

  const { alertState, showError, showSuccess, hideAlert } = useAlert();
  const isEditMode = !!encaissementId;
  const editLoadedRef = useRef<number | null>(null);

  // Draft caching — only for new encaissements (never when editing an existing row).
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    date: string;
    selectedClient: Client | null;
    selectedService: Service | null;
    selectedModePaiement: ModePaiement | null;
    referencePaiement: string;
    typeEncaissement: string;
    motifEncaissement: string;
    statusEncaissement: string;
    selectedFacture: FactureImpayee | null;
    selectedDevis: Devis | null;
    libelle: string;
    montantEncaisse: string;
    reste: string;
    dateEcheance: string;
  }>('encaissement', !isEditMode);

  const resetForm = () => {
    setDate(new Date().toISOString().split('T')[0]);
    setSelectedClient(null);
    setSelectedService(null);
    setSelectedModePaiement(null);
    setReferencePaiement('');
    setTypeEncaissement(TypeEncaissement.TOTALITE);
    setMotifEncaissement(MotifEncaissement.PAIEMENT_FACTURE);
    setStatusEncaissement(StatusEncaissement.EN_COURS);
    setSelectedFacture(null);
    setSelectedDevis(null);
    setDejaEncaisse(0);
    setLibelle('');
    setMontantEncaisse('');
    setReste('');
    setDateEcheance('');
    setFacturesImpayees([]);
  };

  // Fetch dropdown data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsData, servicesData, modesData, projetsData] = await Promise.all([
          clientsAPI.getAllForDropdown(),
          servicesAPI.getAllForDropdown(),
          modesPaiementAPI.getAll(),
          projetsAPI.getAllForDropdown(),
        ]);
        setClients(clientsData);
        setServices(servicesData);
        setModesPaiement(modesData);
        setProjets(projetsData);

        // Create mode: repopulate from a saved draft if one exists.
        if (!encaissementId) {
          const draft = loadDraft();
          if (draft) {
            setDate(draft.date);
            setSelectedClient(draft.selectedClient);
            setSelectedService(draft.selectedService);
            setSelectedModePaiement(draft.selectedModePaiement);
            setReferencePaiement(draft.referencePaiement);
            setTypeEncaissement(draft.typeEncaissement);
            setMotifEncaissement(draft.motifEncaissement);
            setStatusEncaissement(draft.statusEncaissement);
            setSelectedFacture(draft.selectedFacture);
            setSelectedDevis(draft.selectedDevis);
            setLibelle(draft.libelle);
            setMontantEncaisse(draft.montantEncaisse);
            setReste(draft.reste);
            setDateEcheance(draft.dateEcheance);
          }
        }
      } catch (err) {
        console.error('Error fetching dropdown data:', err);
      }
    };
    if (open) {
      fetchData();
    }
  }, [open]);

  // Fetch factures when client changes
  useEffect(() => {
    const fetchFactures = async () => {
      if (selectedClient && motifEncaissement === MotifEncaissement.PAIEMENT_FACTURE) {
        try {
          const factures = await encaissementsAPI.getClientUnpaidFactures(selectedClient.id);
          setFacturesImpayees(factures);
        } catch (err) {
          console.error('Error fetching factures:', err);
          setFacturesImpayees([]);
        }
      } else {
        setFacturesImpayees([]);
      }
    };
    fetchFactures();
  }, [selectedClient, motifEncaissement]);

  // Fetch validé devis when client changes
  useEffect(() => {
    const fetchDevis = async () => {
      if (selectedClient && motifEncaissement === MotifEncaissement.AVANCE_DEVIS) {
        try {
          const devisData = await devisAPI.getAllForDropdown('valide', selectedClient.id);
          setDevisValides(devisData);
        } catch (err) {
          console.error('Error fetching devis:', err);
          setDevisValides([]);
        }
      } else {
        setDevisValides([]);
      }
    };
    fetchDevis();
  }, [selectedClient, motifEncaissement]);

  // Load encaissement data for edit mode (only once per encaissementId)
  useEffect(() => {
    const loadEncaissement = async () => {
      if (!encaissementId || !open) return;
      if (editLoadedRef.current === encaissementId) return;
      if (clients.length === 0 || modesPaiement.length === 0) return;

      editLoadedRef.current = encaissementId;
      try {
        setLoading(true);
        const encaissement = await encaissementsAPI.getById(encaissementId);

        setDate(encaissement.date || new Date().toISOString().split('T')[0]);
        setTypeEncaissement(encaissement.type_encaissement);
        setMotifEncaissement(encaissement.motif_encaissement);
        setStatusEncaissement(encaissement.status || StatusEncaissement.EN_COURS);
        setReferencePaiement(encaissement.reference_paiement || '');
        setLibelle(encaissement.libelle || '');
        setMontantEncaisse(encaissement.montant_encaisse?.toString() || '');
        setReste(encaissement.reste?.toString() || '');
        setDateEcheance(encaissement.date_echeance || '');

        // Set related entities
        if (encaissement.client_id) {
          const client = clients.find(c => c.id === encaissement.client_id);
          setSelectedClient(client || null);
        }
        if (encaissement.service_id) {
          const service = services.find(s => s.id === encaissement.service_id);
          setSelectedService(service || null);
        }
        if (encaissement.mode_paiement_id) {
          const mode = modesPaiement.find(m => m.id === encaissement.mode_paiement_id);
          setSelectedModePaiement(mode || null);
        }
        if (encaissement.devis_id) {
          if (encaissement.client_id) {
            const devisData = await devisAPI.getAllForDropdown('valide', encaissement.client_id);
            setDevisValides(devisData);
            const devis = devisData.find(d => d.id === encaissement.devis_id);
            setSelectedDevis(devis || null);
          }
        }
        if (encaissement.facture_id) {
          if (encaissement.client_id) {
            const factures = await encaissementsAPI.getClientUnpaidFactures(encaissement.client_id);
            setFacturesImpayees(factures);
            const facture = factures.find(f => f.id === encaissement.facture_id);
            setSelectedFacture(facture || null);
          }
        }
      } catch (err) {
        showError('Erreur lors du chargement de l\'encaissement');
      } finally {
        setLoading(false);
      }
    };

    loadEncaissement();
  }, [encaissementId, open, clients, services, modesPaiement, projets]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      editLoadedRef.current = null;
      setDate(new Date().toISOString().split('T')[0]);
      setSelectedClient(null);
      setSelectedService(null);
      setSelectedModePaiement(null);
      setReferencePaiement('');
      setTypeEncaissement(TypeEncaissement.TOTALITE);
      setMotifEncaissement(MotifEncaissement.PAIEMENT_FACTURE);
      setStatusEncaissement(StatusEncaissement.EN_COURS);
      setSelectedFacture(null);
      setSelectedDevis(null);
      setDejaEncaisse(0);
      setLibelle('');
      setMontantEncaisse('');
      setReste('');
      setDateEcheance('');
      setFacturesImpayees([]);
    }
  }, [open]);

  // Fetch total already encaissé when devis or facture changes
  useEffect(() => {
    const fetchDejaEncaisse = async () => {
      if (selectedDevis && motifEncaissement === MotifEncaissement.AVANCE_DEVIS) {
        try {
          const { total_encaisse } = await encaissementsAPI.getTotalEncaisse({
            devis_id: selectedDevis.id,
            exclude_id: encaissementId || undefined,
          });
          setDejaEncaisse(total_encaisse);
        } catch { setDejaEncaisse(0); }
      } else if (selectedFacture && motifEncaissement === MotifEncaissement.PAIEMENT_FACTURE) {
        try {
          const { total_encaisse } = await encaissementsAPI.getTotalEncaisse({
            facture_id: selectedFacture.id,
            exclude_id: encaissementId || undefined,
          });
          setDejaEncaisse(total_encaisse);
        } catch { setDejaEncaisse(0); }
      } else {
        setDejaEncaisse(0);
      }
    };
    fetchDejaEncaisse();
  }, [selectedDevis, selectedFacture, motifEncaissement, encaissementId]);

  // Auto-calculate reste when facture or devis is selected
  useEffect(() => {
    const montant = parseFloat(montantEncaisse) || 0;
    if (selectedFacture && motifEncaissement === MotifEncaissement.PAIEMENT_FACTURE) {
      const total = Number(selectedFacture.total_net) || 0;
      const resteCalc = total - dejaEncaisse - montant;
      setReste(resteCalc > 0 ? resteCalc.toFixed(2) : '0');
    } else if (selectedDevis && motifEncaissement === MotifEncaissement.AVANCE_DEVIS) {
      const total = Math.round(Number(selectedDevis.montant_total) * 1.2 * 100) / 100;
      const resteCalc = total - dejaEncaisse - montant;
      setReste(resteCalc > 0 ? resteCalc.toFixed(2) : '0');
    }
  }, [selectedFacture, selectedDevis, montantEncaisse, motifEncaissement, dejaEncaisse]);

  const handleSubmit = async () => {
    // Validation
    if (!date) {
      showError('La date est obligatoire');
      return;
    }
    if (!selectedClient) {
      showError('Le client est obligatoire');
      return;
    }
    if (!selectedModePaiement) {
      showError('Le mode de paiement est obligatoire');
      return;
    }
    if (!referencePaiement) {
      showError('La référence de paiement est obligatoire');
      return;
    }
    if (!motifEncaissement) {
      showError('Le motif d\'encaissement est obligatoire');
      return;
    }
    if (motifEncaissement === MotifEncaissement.PAIEMENT_FACTURE && !selectedFacture) {
      showError('La facture est obligatoire');
      return;
    }
    if (motifEncaissement === MotifEncaissement.AVANCE_DEVIS && !selectedDevis) {
      showError('Le devis est obligatoire');
      return;
    }
    if (!montantEncaisse || parseFloat(montantEncaisse) <= 0) {
      showError('Le montant encaissé doit être supérieur à 0');
      return;
    }
    if (!dateEcheance) {
      showError('La date d\'échéance est obligatoire');
      return;
    }

    try {
      setLoading(true);
      const formData: EncaissementCreate = {
        date: date,
        client_id: selectedClient?.id || null,
        service_id: selectedService?.id || null,
        mode_paiement_id: selectedModePaiement?.id || null,
        reference_paiement: referencePaiement || null,
        type_encaissement: typeEncaissement,
        motif_encaissement: motifEncaissement,
        status: statusEncaissement,
        facture_id: motifEncaissement === MotifEncaissement.PAIEMENT_FACTURE ? selectedFacture?.id || null : null,
        devis_id: motifEncaissement === MotifEncaissement.AVANCE_DEVIS ? selectedDevis?.id || null : null,
        projet_id: motifEncaissement === MotifEncaissement.AVANCE_DEVIS
          ? projets.find(p => p.devis_id === selectedDevis?.id)?.id || null
          : null,
        libelle: libelle || null,
        montant_encaisse: parseFloat(montantEncaisse),
        reste: reste ? parseFloat(reste) : null,
        date_echeance: dateEcheance || null,
      };

      if (isEditMode) {
        await encaissementsAPI.update(encaissementId!, formData as EncaissementUpdate);
        showSuccess('Encaissement modifié avec succès');
      } else {
        await encaissementsAPI.create(formData);
        showSuccess('Encaissement créé avec succès');
      }

      clearDraft();
      onSuccess();
      onClose();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Annuler = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    resetForm();
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      saveDraft({
        date,
        selectedClient,
        selectedService,
        selectedModePaiement,
        referencePaiement,
        typeEncaissement,
        motifEncaissement,
        statusEncaissement,
        selectedFacture,
        selectedDevis,
        libelle,
        montantEncaisse,
        reste,
        dateEcheance,
      });
      onClose();
    }
  };

  return (
    <>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth TransitionComponent={SlideUpTransition}>
        <DialogTitle sx={{ backgroundColor: '#9C27B0', color: 'white' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {isEditMode ? 'Modifier l\'Encaissement' : 'Nouvel Encaissement'}
            <Box display="flex" alignItems="center">
              <MinimizeButton color="white" onClick={minimize} />
              <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date *"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

              {/* Client (Sources) */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={clients}
                  getOptionLabel={(option) => option.name || ''}
                  value={selectedClient}
                  onChange={(_, newValue) => {
                    setSelectedClient(newValue);
                    setSelectedFacture(null);
                    setSelectedDevis(null);
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Client (Sources) *" size="small" />
                  )}
                />
              </Grid>

              {/* Date d'échéance */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date d'échéance *"
                  type="date"
                  value={dateEcheance}
                  onChange={(e) => setDateEcheance(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Mode de Paiement */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={modesPaiement}
                  getOptionLabel={(option) => option.nom || ''}
                  value={selectedModePaiement}
                  onChange={(_, newValue) => setSelectedModePaiement(newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Mode de Paiement *" size="small" />
                  )}
                />
              </Grid>

              {/* Référence de Paiement */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Référence de Paiement *"
                  value={referencePaiement}
                  onChange={(e) => setReferencePaiement(e.target.value)}
                  size="small"
                  placeholder="N° chèque, N° virement..."
                />
              </Grid>

              {/* Type d'Encaissement */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type d'Encaissement</InputLabel>
                  <Select
                    value={typeEncaissement}
                    label="Type d'Encaissement"
                    onChange={(e) => setTypeEncaissement(e.target.value)}
                  >
                    <MenuItem value={TypeEncaissement.TOTALITE}>Totalité</MenuItem>
                    <MenuItem value={TypeEncaissement.PARTIEL}>Partiel</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Motif d'Encaissement */}
              <Grid item xs={12} sm={showStatus ? 4 : 6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Motif d'Encaissement *</InputLabel>
                  <Select
                    value={motifEncaissement}
                    label="Motif d'Encaissement *"
                    onChange={(e) => {
                      setMotifEncaissement(e.target.value);
                      setSelectedFacture(null);
                      setSelectedDevis(null);
                    }}
                  >
                    <MenuItem value={MotifEncaissement.PAIEMENT_FACTURE}>Paiement Facture</MenuItem>
                    <MenuItem value={MotifEncaissement.AVANCE_DEVIS}>Avance Devis</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Statut - only shown when opened from Rapprochement Bancaire */}
              {showStatus && (
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Statut *</InputLabel>
                    <Select
                      value={statusEncaissement}
                      label="Statut *"
                      onChange={(e) => setStatusEncaissement(e.target.value)}
                    >
                      <MenuItem value={StatusEncaissement.EN_COURS}>En Cours</MenuItem>
                      <MenuItem value={StatusEncaissement.ENCAISSE}>Encaissé</MenuItem>
                      <MenuItem value={StatusEncaissement.IMPAYE}>Impayé</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Facture (conditional - only if motif is paiement_facture) */}
              {motifEncaissement === MotifEncaissement.PAIEMENT_FACTURE && (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={facturesImpayees}
                    getOptionLabel={(option) =>
                      `${option.reference} - ${Number(option.total_net).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH`
                    }
                    value={selectedFacture}
                    onChange={(_, newValue) => {
                      setSelectedFacture(newValue);
                      if (newValue?.designations) {
                        setLibelle(newValue.designations);
                      } else {
                        setLibelle('');
                      }
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disabled={!selectedClient}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Facture *"
                        size="small"
                        helperText={!selectedClient ? "Sélectionnez d'abord un client" : ''}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {option.reference}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(option.date).toLocaleDateString('fr-FR')} - {Number(option.total_net).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                </Grid>
              )}

              {/* Devis (conditional - only if motif is avance_devis) */}
              {motifEncaissement === MotifEncaissement.AVANCE_DEVIS && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={devisValides}
                      getOptionLabel={(option) =>
                        `${option.reference} - ${(Math.round(Number(option.montant_total) * 1.2 * 100) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH TTC`
                      }
                      value={selectedDevis}
                      onChange={(_, newValue) => {
                        setSelectedDevis(newValue);
                        if (newValue?.items?.length) {
                          setLibelle(newValue.items.map(i => i.designation).join(', '));
                        } else {
                          setLibelle('');
                        }
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      disabled={!selectedClient}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Devis *"
                          size="small"
                          helperText={!selectedClient ? "Sélectionnez d'abord un client" : ''}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {option.reference}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(option.date).toLocaleDateString('fr-FR')} - {(Math.round(Number(option.montant_total) * 1.2 * 100) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH TTC
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    />
                  </Grid>
                  {selectedDevis && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Projet (auto)"
                        size="small"
                        multiline
                        minRows={1}
                        maxRows={3}
                        value={
                          (() => {
                            const projet = projets.find(p => p.devis_id === selectedDevis.id);
                            return projet ? `${projet.code} - ${projet.nom}` : 'Aucun projet lié';
                          })()
                        }
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiInputBase-input': { backgroundColor: 'background.default' } }}
                      />
                    </Grid>
                  )}
                </>
              )}

              {/* Libellé */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Libellé"
                  value={libelle}
                  onChange={(e) => setLibelle(e.target.value)}
                  size="small"
                  multiline
                  rows={2}
                  placeholder="Description ou notes..."
                />
              </Grid>

              {/* Montant Restant Devis (read-only) */}
              {motifEncaissement === MotifEncaissement.AVANCE_DEVIS && selectedDevis && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Montant Restant Devis (TTC)"
                    size="small"
                    value={(Math.round(Number(selectedDevis.montant_total) * 1.2 * 100) / 100 - dejaEncaisse).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                    InputProps={{
                      readOnly: true,
                      endAdornment: <InputAdornment position="end">DH</InputAdornment>,
                    }}
                    InputLabelProps={{ shrink: true }}
                    helperText={dejaEncaisse > 0 ? `Déjà encaissé: ${dejaEncaisse.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH sur ${(Math.round(Number(selectedDevis.montant_total) * 1.2 * 100) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH TTC` : ''}
                    sx={{ '& .MuiInputBase-input': { backgroundColor: 'background.default' } }}
                  />
                </Grid>
              )}

              {/* Montant Restant Facture (read-only) */}
              {motifEncaissement === MotifEncaissement.PAIEMENT_FACTURE && selectedFacture && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Montant Restant Facture"
                    size="small"
                    value={(Number(selectedFacture.total_net) - dejaEncaisse).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                    InputProps={{
                      readOnly: true,
                      endAdornment: <InputAdornment position="end">DH</InputAdornment>,
                    }}
                    InputLabelProps={{ shrink: true }}
                    helperText={dejaEncaisse > 0 ? `Déjà encaissé: ${dejaEncaisse.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH sur ${Number(selectedFacture.total_net).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH` : ''}
                    sx={{ '& .MuiInputBase-input': { backgroundColor: 'background.default' } }}
                  />
                </Grid>
              )}

              {/* Montant Encaissé */}
              <Grid item xs={12} sm={(selectedDevis && motifEncaissement === MotifEncaissement.AVANCE_DEVIS) || (selectedFacture && motifEncaissement === MotifEncaissement.PAIEMENT_FACTURE) ? 4 : 6}>
                <TextField
                  fullWidth
                  label="Montant Encaissé *"
                  value={montantEncaisse}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d*$/.test(value)) {
                      setMontantEncaisse(value);
                    }
                  }}
                  size="small"
                  type="text"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">DH</InputAdornment>,
                  }}
                />
              </Grid>

              {/* Reste */}
              <Grid item xs={12} sm={(selectedDevis && motifEncaissement === MotifEncaissement.AVANCE_DEVIS) || (selectedFacture && motifEncaissement === MotifEncaissement.PAIEMENT_FACTURE) ? 4 : 6}>
                <TextField
                  fullWidth
                  label="Reste"
                  value={reste}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d*$/.test(value)) {
                      setReste(value);
                    }
                  }}
                  size="small"
                  type="text"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">DH</InputAdornment>,
                  }}
                />
              </Grid>

              {/* Info box for selected facture */}
              {selectedFacture && (
                <Grid item xs={12}>
                  <Box sx={{
                    p: 2,
                    backgroundColor: 'rgba(156, 39, 176, 0.08)',
                    borderRadius: 1,
                    border: '1px solid rgba(156, 39, 176, 0.2)'
                  }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Facture sélectionnée
                    </Typography>
                    <Typography variant="body2">
                      <strong>Référence:</strong> {selectedFacture.reference}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Date:</strong> {new Date(selectedFacture.date).toLocaleDateString('fr-FR')}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Montant Total:</strong> {Number(selectedFacture.total_net).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              backgroundColor: '#9C27B0',
              '&:hover': { backgroundColor: '#7B1FA2' },
            }}
          >
            {loading ? 'Enregistrement...' : isEditMode ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={open && minimized}
        color="#9C27B0"
        title={isEditMode ? 'Modifier l\'Encaissement' : 'Nouvel Encaissement'}
        onRestore={restore}
        onClose={handleClose}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </>
  );
};

export default EncaissementFormDialog;
